// src/components/jobs/VAJobBrowse.jsx
import React, { useState, useEffect } from 'react';
import { 
  Search, Filter, Bookmark, Clock, DollarSign, MapPin, 
  Star, User, Calendar, Target, Zap, Heart, Eye, CheckCircle,
  ChevronDown, ChevronUp, SlidersHorizontal, AlertCircle
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { jobService } from '../../services/jobService';

const VAJobBrowse = () => {
  const { userProfile, initialLoading } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savedJobs, setSavedJobs] = useState([]);
  const [appliedJobs, setAppliedJobs] = useState([]);
  
  const [filters, setFilters] = useState({
    search: '',
    category: 'all',
    budgetMin: '',
    budgetMax: '',
    budgetType: 'all',
    experienceLevel: 'all',
    urgency: 'all',
    location: 'all',
    skills: [],
    postedWithin: 'all',
    sortBy: 'newest'
  });

  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('card'); // card, list

  const categories = [
    'Administrative Support', 'Data Entry', 'Customer Service', 
    'Content Creation', 'Social Media Management', 'Bookkeeping',
    'Research', 'Email Management', 'Virtual Reception', 
    'Project Management', 'Marketing Support', 'Other'
  ];

  const popularSkills = [
    'Microsoft Office', 'Google Workspace', 'Data Entry', 'Email Management',
    'Customer Service', 'Social Media', 'Content Writing', 'Research',
    'Bookkeeping', 'QuickBooks', 'CRM Management', 'WordPress'
  ];

  useEffect(() => {
    if (!initialLoading && userProfile?.uid) {
      loadJobs();
      loadUserJobData();
    }
  }, [userProfile, initialLoading, filters]);

  const loadJobs = async () => {
    try {
      setLoading(true);
      let jobsData = await jobService.getJobs({ status: 'open' });

      // Apply filters
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        jobsData = jobsData.filter(job => 
          job.title.toLowerCase().includes(searchLower) ||
          job.description.toLowerCase().includes(searchLower) ||
          job.skillsRequired?.some(skill => skill.toLowerCase().includes(searchLower))
        );
      }

      if (filters.category !== 'all') {
        jobsData = jobsData.filter(job => job.category === filters.category);
      }

      if (filters.budgetMin) {
        jobsData = jobsData.filter(job => job.budget >= parseFloat(filters.budgetMin));
      }

      if (filters.budgetMax) {
        jobsData = jobsData.filter(job => job.budget <= parseFloat(filters.budgetMax));
      }

      if (filters.budgetType !== 'all') {
        jobsData = jobsData.filter(job => job.budgetType === filters.budgetType);
      }

      if (filters.experienceLevel !== 'all') {
        jobsData = jobsData.filter(job => job.experienceLevel === filters.experienceLevel);
      }

      if (filters.urgency !== 'all') {
        jobsData = jobsData.filter(job => job.urgency === filters.urgency);
      }

      if (filters.skills.length > 0) {
        jobsData = jobsData.filter(job => 
          filters.skills.some(skill => job.skillsRequired?.includes(skill))
        );
      }

      if (filters.postedWithin !== 'all') {
        const now = new Date();
        const daysAgo = {
          '1': 1, '3': 3, '7': 7, '14': 14, '30': 30
        }[filters.postedWithin];
        
        if (daysAgo) {
          const cutoffDate = new Date(now.getTime() - (daysAgo * 24 * 60 * 60 * 1000));
          jobsData = jobsData.filter(job => new Date(job.createdAt) >= cutoffDate);
        }
      }

      // Sort jobs
      switch (filters.sortBy) {
        case 'newest':
          jobsData.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
          break;
        case 'oldest':
          jobsData.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
          break;
        case 'budget-high':
          jobsData.sort((a, b) => b.budget - a.budget);
          break;
        case 'budget-low':
          jobsData.sort((a, b) => a.budget - b.budget);
          break;
        case 'deadline':
          jobsData.sort((a, b) => {
            if (!a.deadline) return 1;
            if (!b.deadline) return -1;
            return new Date(a.deadline) - new Date(b.deadline);
          });
          break;
        case 'applications':
          jobsData.sort((a, b) => (a.applications?.length || 0) - (b.applications?.length || 0));
          break;
      }

      setJobs(jobsData);
    } catch (error) {
      setError('Failed to load jobs');
      console.error('Error loading jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUserJobData = async () => {
    // Load saved jobs and applied jobs for this VA
    // This would typically come from the VA's profile or a separate collection
    try {
      const vaProfile = await jobService.getVAProfile(userProfile.uid);
      if (vaProfile) {
        setSavedJobs(vaProfile.savedJobs || []);
        setAppliedJobs(vaProfile.appliedJobs || []);
      }
    } catch (error) {
      console.error('Error loading user job data:', error);
    }
  };

  const handleSaveJob = async (jobId) => {
    try {
      const isCurrentlySaved = savedJobs.includes(jobId);
      const updatedSavedJobs = isCurrentlySaved 
        ? savedJobs.filter(id => id !== jobId)
        : [...savedJobs, jobId];
      
      setSavedJobs(updatedSavedJobs);
      
      // Update in database
      await jobService.updateVAProfile(userProfile.uid, {
        savedJobs: updatedSavedJobs
      });
    } catch (error) {
      console.error('Error saving/unsaving job:', error);
      // Revert optimistic update
      loadUserJobData();
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleSkillToggle = (skill) => {
    setFilters(prev => ({
      ...prev,
      skills: prev.skills.includes(skill)
        ? prev.skills.filter(s => s !== skill)
        : [...prev.skills, skill]
    }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      category: 'all',
      budgetMin: '',
      budgetMax: '',
      budgetType: 'all',
      experienceLevel: 'all',
      urgency: 'all',
      location: 'all',
      skills: [],
      postedWithin: 'all',
      sortBy: 'newest'
    });
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (filters.search) count++;
    if (filters.category !== 'all') count++;
    if (filters.budgetMin || filters.budgetMax) count++;
    if (filters.budgetType !== 'all') count++;
    if (filters.experienceLevel !== 'all') count++;
    if (filters.urgency !== 'all') count++;
    if (filters.location !== 'all') count++;
    if (filters.skills.length > 0) count++;
    if (filters.postedWithin !== 'all') count++;
    return count;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Browse Jobs</h1>
          <p className="text-gray-600">Find your next virtual assistant opportunity</p>
        </div>
        
        <div className="flex items-center space-x-3">
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('card')}
              className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                viewMode === 'card' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
              }`}
            >
              Cards
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                viewMode === 'list' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
              }`}
            >
              List
            </button>
          </div>
        </div>
      </div>

      {/* Search & Quick Filters */}
      <div className="bg-white rounded-lg border p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search jobs by title, description, or skills..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          
          <div className="flex flex-wrap gap-3">
            <select
              value={filters.category}
              onChange={(e) => handleFilterChange('category', e.target.value)}
              className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Categories</option>
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
            
            <select
              value={filters.sortBy}
              onChange={(e) => handleFilterChange('sortBy', e.target.value)}
              className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="budget-high">Highest Budget</option>
              <option value="budget-low">Lowest Budget</option>
              <option value="deadline">By Deadline</option>
              <option value="applications">Fewest Applications</option>
            </select>
            
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <SlidersHorizontal size={16} className="mr-2" />
              Filters
              {getActiveFilterCount() > 0 && (
                <span className="ml-2 bg-blue-600 text-white text-xs rounded-full px-2 py-1">
                  {getActiveFilterCount()}
                </span>
              )}
              {showFilters ? <ChevronUp className="ml-2" size={16} /> : <ChevronDown className="ml-2" size={16} />}
            </button>
          </div>
        </div>
      </div>

      {/* Advanced Filters */}
      {showFilters && (
        <div className="bg-white rounded-lg border p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Advanced Filters</h3>
            <button
              onClick={clearFilters}
              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              Clear All Filters
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Budget Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Budget Range (ZAR)</label>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="number"
                  placeholder="Min"
                  value={filters.budgetMin}
                  onChange={(e) => handleFilterChange('budgetMin', e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <input
                  type="number"
                  placeholder="Max"
                  value={filters.budgetMax}
                  onChange={(e) => handleFilterChange('budgetMax', e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Budget Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Budget Type</label>
              <select
                value={filters.budgetType}
                onChange={(e) => handleFilterChange('budgetType', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Types</option>
                <option value="fixed">Fixed Price</option>
                <option value="hourly">Hourly Rate</option>
              </select>
            </div>

            {/* Experience Level */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Experience Level</label>
              <select
                value={filters.experienceLevel}
                onChange={(e) => handleFilterChange('experienceLevel', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Levels</option>
                <option value="entry">Entry Level</option>
                <option value="intermediate">Intermediate</option>
                <option value="expert">Expert</option>
              </select>
            </div>

            {/* Urgency */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Urgency</label>
              <select
                value={filters.urgency}
                onChange={(e) => handleFilterChange('urgency', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Urgency Levels</option>
                <option value="low">Low Priority</option>
                <option value="medium">Medium Priority</option>
                <option value="high">High Priority</option>
              </select>
            </div>

            {/* Posted Within */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Posted Within</label>
              <select
                value={filters.postedWithin}
                onChange={(e) => handleFilterChange('postedWithin', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">Any Time</option>
                <option value="1">Last 24 hours</option>
                <option value="3">Last 3 days</option>
                <option value="7">Last week</option>
                <option value="14">Last 2 weeks</option>
                <option value="30">Last month</option>
              </select>
            </div>
          </div>

          {/* Skills Filter */}
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">Skills</label>
            <div className="flex flex-wrap gap-2">
              {popularSkills.map(skill => (
                <button
                  key={skill}
                  onClick={() => handleSkillToggle(skill)}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                    filters.skills.includes(skill)
                      ? 'bg-blue-100 text-blue-800 border border-blue-300'
                      : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
                  }`}
                >
                  {skill}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Results Header */}
      <div className="flex justify-between items-center">
        <div className="text-gray-600">
          Showing {jobs.length} job{jobs.length !== 1 ? 's' : ''}
          {getActiveFilterCount() > 0 && (
            <span className="ml-2 text-blue-600 font-medium">
              with {getActiveFilterCount()} filter{getActiveFilterCount() !== 1 ? 's' : ''} applied
            </span>
          )}
        </div>
      </div>

      {/* Jobs List */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="text-red-500 mr-2" size={20} />
            <p className="text-red-800">{error}</p>
          </div>
        </div>
      )}

      {jobs.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border">
          <Search className="mx-auto h-16 w-16 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No jobs found</h3>
          <p className="text-gray-600 mb-4">
            Try adjusting your search criteria or check back later for new opportunities.
          </p>
          {getActiveFilterCount() > 0 && (
            <button
              onClick={clearFilters}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Clear all filters
            </button>
          )}
        </div>
      ) : (
        <div className={`space-y-4 ${viewMode === 'list' ? 'space-y-2' : ''}`}>
          {jobs.map(job => (
            <VAJobCard 
              key={job.id} 
              job={job} 
              viewMode={viewMode}
              isSaved={savedJobs.includes(job.id)}
              hasApplied={appliedJobs.includes(job.id)}
              onSave={() => handleSaveJob(job.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// VA Job Card Component
const VAJobCard = ({ job, viewMode, isSaved, hasApplied, onSave }) => {
  const [expanded, setExpanded] = useState(false);

  const formatDate = (dateString) => {
    if (!dateString) return 'No deadline';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = date - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'Overdue';
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays <= 7) return `${diffDays} days`;
    return date.toLocaleDateString('en-ZA');
  };

  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = now - date;
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffHours < 1) return 'Just posted';
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays <= 7) return `${diffDays} days ago`;
    return date.toLocaleDateString('en-ZA');
  };

  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'high': return 'text-red-600 bg-red-50 border-red-200';
      case 'medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low': return 'text-green-600 bg-green-50 border-green-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  if (viewMode === 'list') {
    return (
      <div className="bg-white border rounded-lg p-4 hover:shadow-md transition-shadow">
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1">
                <Link to={`/jobs/${job.id}`} className="hover:text-blue-600">
                  <h3 className="text-lg font-semibold text-gray-900 line-clamp-1">{job.title}</h3>
                </Link>
                <p className="text-gray-600 text-sm line-clamp-1">{job.description}</p>
              </div>
              
              <div className="flex items-center space-x-2 ml-4">
                <button
                  onClick={onSave}
                  className={`p-2 rounded-full transition-colors ${
                    isSaved 
                      ? 'text-red-500 bg-red-50 hover:bg-red-100' 
                      : 'text-gray-400 hover:text-red-500 hover:bg-red-50'
                  }`}
                >
                  <Heart size={16} fill={isSaved ? 'currentColor' : 'none'} />
                </button>
                
                <span className={`px-2 py-1 border rounded-full text-xs font-medium ${getUrgencyColor(job.urgency)}`}>
                  {job.urgency?.charAt(0).toUpperCase() + job.urgency?.slice(1)} Priority
                </span>
              </div>
            </div>
            
            <div className="flex items-center text-sm text-gray-600 space-x-4">
              <div className="flex items-center">
                <DollarSign size={14} className="mr-1" />
                R{job.budget?.toLocaleString()} {job.budgetType === 'hourly' && '/hr'}
              </div>
              <div className="flex items-center">
                <Calendar size={14} className="mr-1" />
                Due {formatDate(job.deadline)}
              </div>
              <div className="flex items-center">
                <User size={14} className="mr-1" />
                {job.applications?.length || 0} applicants
              </div>
              <div className="flex items-center">
                <Clock size={14} className="mr-1" />
                {getTimeAgo(job.createdAt)}
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-3 ml-6">
            <Link
              to={`/jobs/${job.id}`}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
            >
              View Details
            </Link>
            {!hasApplied ? (
              <Link
                to={`/jobs/${job.id}/apply`}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                Apply Now
              </Link>
            ) : (
              <span className="px-4 py-2 bg-green-100 text-green-800 rounded-lg text-sm font-medium">
                Applied
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border rounded-lg p-6 hover:shadow-md transition-shadow">
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <div className="flex items-start justify-between mb-2">
            <div>
              <Link to={`/jobs/${job.id}`} className="hover:text-blue-600">
                <h3 className="text-xl font-semibold text-gray-900 mb-1">{job.title}</h3>
              </Link>
              <div className="flex items-center space-x-3 text-sm text-gray-600">
                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-medium">
                  {job.category}
                </span>
                <span className={`px-2 py-1 border rounded-full font-medium ${getUrgencyColor(job.urgency)}`}>
                  {job.urgency?.charAt(0).toUpperCase() + job.urgency?.slice(1)} Priority
                </span>
                <span className="text-gray-500">{getTimeAgo(job.createdAt)}</span>
              </div>
            </div>
            
            <button
              onClick={onSave}
              className={`p-2 rounded-full transition-colors ${
                isSaved 
                  ? 'text-red-500 bg-red-50 hover:bg-red-100' 
                  : 'text-gray-400 hover:text-red-500 hover:bg-red-50'
              }`}
            >
              <Heart size={20} fill={isSaved ? 'currentColor' : 'none'} />
            </button>
          </div>
          
          <p className="text-gray-700 mb-4 line-clamp-2">{job.description}</p>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div className="flex items-center text-gray-600">
              <DollarSign size={16} className="mr-2 text-gray-400" />
              <div>
                <div className="font-semibold text-gray-900">R{job.budget?.toLocaleString()}</div>
                <div className="text-xs">{job.budgetType === 'hourly' ? 'Per hour' : 'Fixed price'}</div>
              </div>
            </div>
            
            <div className="flex items-center text-gray-600">
              <Calendar size={16} className="mr-2 text-gray-400" />
              <div>
                <div className="font-semibold text-gray-900">{formatDate(job.deadline)}</div>
                <div className="text-xs">Deadline</div>
              </div>
            </div>
            
            <div className="flex items-center text-gray-600">
              <User size={16} className="mr-2 text-gray-400" />
              <div>
                <div className="font-semibold text-gray-900">{job.applications?.length || 0}</div>
                <div className="text-xs">Applicants</div>
              </div>
            </div>
            
            <div className="flex items-center text-gray-600">
              <Target size={16} className="mr-2 text-gray-400" />
              <div>
                <div className="font-semibold text-gray-900 capitalize">{job.experienceLevel}</div>
                <div className="text-xs">Experience</div>
              </div>
            </div>
          </div>
          
          {job.skillsRequired && job.skillsRequired.length > 0 && (
            <div className="mb-4">
              <div className="flex flex-wrap gap-2">
                {job.skillsRequired.slice(0, expanded ? job.skillsRequired.length : 4).map(skill => (
                  <span key={skill} className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm">
                    {skill}
                  </span>
                ))}
                {job.skillsRequired.length > 4 && (
                  <button
                    onClick={() => setExpanded(!expanded)}
                    className="text-blue-600 hover:text-blue-700 text-sm font-medium px-3 py-1"
                  >
                    {expanded ? 'Show less' : `+${job.skillsRequired.length - 4} more`}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      
      <div className="flex justify-between items-center pt-4 border-t border-gray-200">
        <div className="flex items-center text-sm text-gray-600">
          <MapPin size={14} className="mr-1" />
          {job.location || 'Remote'}
          {job.expectedDuration && (
            <>
              <span className="mx-2">•</span>
              <Clock size={14} className="mr-1" />
              {job.expectedDuration}
            </>
          )}
        </div>
        
        <div className="flex space-x-3">
          <Link
            to={`/jobs/${job.id}`}
            className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <Eye size={16} className="mr-2" />
            View Details
          </Link>
          {!hasApplied ? (
            <Link
              to={`/jobs/${job.id}/apply`}
              className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Apply Now
            </Link>
          ) : (
            <span className="flex items-center px-6 py-2 bg-green-100 text-green-800 rounded-lg font-medium">
              <CheckCircle size={16} className="mr-2" />
              Applied
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default VAJobBrowse;